/**
 * Agent Store — 会话与消息由后端 /api/agent/* 持久化（SQLite）
 * 发送走 SSE：交错 text / tool / approval；支持 Todo / Auto / 续跑
 */
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { agentApi } from '../api'
import { createPartsAccumulator, runAgentStream } from '../utils/agentStream'
import { agentClientHeaders, getAgentClientId } from '../utils/agentClientId'
import { collectPageContext } from './pageContext'

const AUTO_PUBLISH_KEY = 'mediapilot.agent.autoPublish'
const PAGE_CONTEXT_KEY = 'mediapilot.agent.sendPageContext'

function readFlag(key, fallback) {
  try {
    const v = localStorage.getItem(key)
    return v == null ? fallback : v === '1'
  } catch {
    return fallback
  }
}

function writeFlag(key, value) {
  try {
    localStorage.setItem(key, value ? '1' : '0')
  } catch {
    /* ignore */
  }
}

const tasks = ref([])
const currentTaskId = ref(null)
// 侧栏是否打开（Layout 与首页命令框共用）
const panelOpen = ref(false)
let sessionsPromise = null
const isProcessing = ref(false)
const autoMode = ref(true)
// 自动发布：发布类工具免审批（默认关闭，用户显式打开后记住）
const autoPublish = ref(readFlag(AUTO_PUBLISH_KEY, false))
// 随消息附带当前页面内容
const sendPageContext = ref(readFlag(PAGE_CONTEXT_KEY, true))
watch(autoPublish, (v) => writeFlag(AUTO_PUBLISH_KEY, v))
watch(sendPageContext, (v) => writeFlag(PAGE_CONTEXT_KEY, v))
const activeTab = ref('chat') // chat | steps | logs
const runStartedAt = ref(null)
const currentTodos = ref([])
const currentLogs = ref([])
const currentRunId = ref(null)
const currentTurn = ref(null)
let abortCtrl = null
let timerHandle = null
const elapsedSec = ref(0)

function startTimer() {
  stopTimer()
  runStartedAt.value = Date.now()
  elapsedSec.value = 0
  timerHandle = setInterval(() => {
    if (runStartedAt.value) {
      elapsedSec.value = Math.floor((Date.now() - runStartedAt.value) / 1000)
    }
  }, 500)
}

function stopTimer() {
  if (timerHandle) {
    clearInterval(timerHandle)
    timerHandle = null
  }
}

function formatElapsed(sec) {
  const s = Math.max(0, sec | 0)
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

function mapServerMessage(m) {
  if (m.role === 'user') {
    return { role: 'user', content: m.content || '' }
  }
  const steps = m.steps || []
  let parts = m.parts
  if (!parts?.length) {
    parts = steps.length
      ? [
          ...steps.map((s, i) => ({
            kind: 'tool',
            id: `hist_${i}`,
            tool: {
              id: `hist_${i}`,
              name: s.name,
              label: s.name,
              status: s.status === 'failed' ? 'error' : s.status === 'running' ? 'running' : 'success',
              summary: s.result || '',
              args: {},
            },
          })),
          ...(m.text || m.content ? [{ kind: 'text', content: m.text || m.content }] : []),
        ]
      : m.text || m.content
        ? [{ kind: 'text', content: m.text || m.content }]
        : []
  }
  return {
    role: 'agent',
    text: m.text || m.content || '',
    steps,
    articles: m.articles || [],
    parts,
    todos: m.todos || [],
    logs: m.logs || [],
    run_id: m.run_id || null,
    status: m.status || null,
    streaming: false,
    canResume: m.status === 'failed' || m.status === 'interrupted',
  }
}

function hydrateTaskRow(row, messages = []) {
  return {
    id: row.id,
    title: row.title || '对话',
    status: row.status || 'idle',
    time: row.updated_at
      ? new Date(row.updated_at).getTime()
      : row.created_at
        ? new Date(row.created_at).getTime()
        : Date.now(),
    messages,
  }
}

export function useAgentStore() {
  const currentTask = computed(() => tasks.value.find((t) => t.id === currentTaskId.value))
  const workingLabel = computed(() => {
    if (!isProcessing.value) return ''
    return `Working ${formatElapsed(elapsedSec.value)}`
  })
  const stepsCount = computed(() => {
    const n = (currentTodos.value || []).length
    const tools = (currentTask.value?.messages || [])
      .filter((m) => m.role === 'agent')
      .flatMap((m) => (m.parts || []).filter((p) => p.kind === 'tool'))
    return n || tools.length
  })

  /** 只加载一次会话列表；侧栏重复打开不会冲掉进行中的流式消息 */
  const ensureSessions = () => {
    if (!sessionsPromise) {
      sessionsPromise = loadSessions().catch((e) => {
        sessionsPromise = null
        throw e
      })
    }
    return sessionsPromise
  }

  const openPanel = () => {
    panelOpen.value = true
  }

  /** 新开一个对话并发送（首页命令框、各页「问 Agent」入口用） */
  const askInNewConversation = async (text) => {
    const t = text?.trim()
    if (!t) return
    panelOpen.value = true
    if (isProcessing.value) {
      ElMessage.warning('Agent 正在执行上一个任务，请稍候或先停止')
      return
    }
    await ensureSessions()
    const cur = currentTask.value
    if (!cur || cur.messages.length) {
      await createTask(t.length > 30 ? `${t.slice(0, 30)}…` : t)
    }
    try {
      await sendMessage(t)
    } catch (_) {
      /* sendMessage 内已提示 */
    }
  }

  const loadSessions = async () => {
    try {
      const res = await agentApi.listSessions()
      const items = res.items || []
      tasks.value = items.map((i) => hydrateTaskRow(i, []))
      if (tasks.value.length === 0) {
        await createTask('新对话')
        return
      }
      if (!currentTaskId.value || !tasks.value.some((t) => t.id === currentTaskId.value)) {
        currentTaskId.value = tasks.value[0].id
      }
      await selectTask(currentTaskId.value)
    } catch (e) {
      ElMessage.error('加载会话失败: ' + (e.message || ''))
    }
  }

  const createTask = async (title) => {
    const res = await agentApi.createSession({ title: title || '新对话' })
    const t = hydrateTaskRow(res, [])
    tasks.value.unshift(t)
    currentTaskId.value = t.id
    currentTodos.value = []
    currentLogs.value = []
    currentRunId.value = null
    return t
  }

  const selectTask = async (id) => {
    currentTaskId.value = id
    try {
      const res = await agentApi.getSession(id)
      const t = tasks.value.find((x) => x.id === id)
      if (t) {
        t.title = res.title
        t.messages = (res.messages || []).map(mapServerMessage)
        t.time = res.updated_at ? new Date(res.updated_at).getTime() : t.time
        const lastAgent = [...t.messages].reverse().find((m) => m.role === 'agent')
        currentTodos.value = lastAgent?.todos || []
        currentLogs.value = lastAgent?.logs || []
        currentRunId.value = lastAgent?.run_id || null
      }
    } catch (e) {
      ElMessage.error('加载对话失败: ' + (e.message || ''))
    }
  }

  const stopStream = () => {
    if (abortCtrl) {
      abortCtrl.abort()
      abortCtrl = null
    }
  }

  const sendMessage = async (text, opts = {}) => {
    const t = text?.trim()
    if (!t || isProcessing.value) return

    if (!currentTaskId.value) {
      await createTask('新对话')
    }
    let cur = tasks.value.find((x) => x.id === currentTaskId.value)
    if (!cur) {
      await createTask('新对话')
      cur = tasks.value.find((x) => x.id === currentTaskId.value)
    }

    const resume = !!opts.resume
    const initialTodos = opts.initialTodos || (resume ? currentTodos.value : undefined)
    const runId = opts.runId || (resume ? currentRunId.value : undefined)

    cur.messages.push({ role: 'user', content: t })
    const agentMsg = {
      role: 'agent',
      text: '',
      steps: [],
      articles: [],
      parts: [],
      todos: [],
      logs: [],
      run_id: runId || null,
      streaming: true,
      canResume: false,
    }
    cur.messages.push(agentMsg)
    cur.status = 'running'
    isProcessing.value = true
    startTimer()
    if (!resume) {
      currentTodos.value = []
      currentLogs.value = []
    }

    const acc = createPartsAccumulator()
    agentMsg.parts = acc.parts
    abortCtrl = new AbortController()

    let pageContext
    if (sendPageContext.value) {
      try {
        pageContext = collectPageContext()
      } catch (e) {
        console.warn('[agent] collect page context failed', e)
      }
    }

    try {
      await runAgentStream(
        '/api/agent/chat/stream',
        {
          message: t,
          session_id: currentTaskId.value,
          client_id: getAgentClientId(),
          auto_mode: autoMode.value,
          auto_publish: autoPublish.value,
          page_context: pageContext,
          resume,
          run_id: runId || undefined,
          initial_todos: initialTodos || undefined,
        },
        {
          onTextDelta: (chunk) => {
            acc.onTextDelta(chunk)
            agentMsg.text += chunk
          },
          onToolStart: (tool) => {
            acc.onToolStart(tool)
            agentMsg.steps.push({
              name: tool.label || tool.name,
              status: 'running',
              result: null,
            })
            currentLogs.value = [
              ...currentLogs.value,
              { type: 'tool_start', name: tool.name, label: tool.label, at: Date.now() },
            ]
          },
          onToolComplete: (tool) => {
            acc.onToolComplete(tool)
            const step = agentMsg.steps.find(
              (s) => s.name === (tool.label || tool.name) && s.status === 'running',
            )
            if (step) {
              step.status = tool.status === 'error' ? 'failed' : 'completed'
              step.result = tool.summary || ''
            }
            currentLogs.value = [
              ...currentLogs.value,
              {
                type: 'tool_end',
                name: tool.name,
                status: tool.status,
                summary: tool.summary,
                at: Date.now(),
              },
            ]
          },
          onTodos: (items) => {
            currentTodos.value = items || []
            agentMsg.todos = items || []
          },
          onApprovalRequest: (ev) => {
            acc.onApprovalRequest(ev)
            currentLogs.value = [
              ...currentLogs.value,
              { type: 'approval_request', name: ev.name, approvalId: ev.approvalId, at: Date.now() },
            ]
          },
          onTurn: (ev) => {
            currentTurn.value = ev
            currentLogs.value = [
              ...currentLogs.value,
              { type: 'turn', index: ev.index, maxTurns: ev.maxTurns, at: Date.now() },
            ]
          },
          onUsage: (ev) => {
            currentLogs.value = [
              ...currentLogs.value,
              { type: 'usage', usage: ev.usage, model: ev.model, at: Date.now() },
            ]
          },
          onRunMeta: (ev) => {
            if (ev.runId) {
              currentRunId.value = ev.runId
              agentMsg.run_id = ev.runId
            }
          },
          onCard: (cardType, data) => {
            if (cardType === 'articles' && Array.isArray(data)) {
              agentMsg.articles = data
            }
          },
          onComplete: (result) => {
            if (result.session_id) {
              currentTaskId.value = result.session_id
            }
            if (result.response && !agentMsg.text) {
              agentMsg.text = result.response
              if (!acc.parts.some((p) => p.kind === 'text')) {
                acc.onTextDelta(result.response)
              }
            }
            if (result.articles?.length) agentMsg.articles = result.articles
            if (result.steps?.length) agentMsg.steps = result.steps
            if (result.todos) {
              agentMsg.todos = result.todos
              currentTodos.value = result.todos
            }
            if (result.logs) {
              agentMsg.logs = result.logs
              currentLogs.value = result.logs
            }
            if (result.parts?.length) {
              // 服务端落库 parts 优先（含审批态）
              agentMsg.parts = result.parts
            }
            if (result.run_id) {
              currentRunId.value = result.run_id
              agentMsg.run_id = result.run_id
            }
            const st = result.agent_run_status || result.status || 'completed'
            agentMsg.status = st
            agentMsg.canResume = st === 'failed' || st === 'interrupted'
            const row = tasks.value.find((x) => x.id === (result.session_id || cur.id)) || cur
            row.status = st
            row.time = Date.now()
          },
          onError: (err) => {
            acc.onTextDelta(`\n**出错：** ${err.message || err}`)
            agentMsg.text += `\n出错：${err.message || err}`
            agentMsg.canResume = true
            agentMsg.status = 'failed'
            currentLogs.value = [
              ...currentLogs.value,
              { type: 'error', error: String(err.message || err), at: Date.now() },
            ]
          },
        },
        { signal: abortCtrl.signal, headers: agentClientHeaders() },
      )
      return { response: agentMsg.text, articles: agentMsg.articles }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        ElMessage.error('请求失败: ' + (e.message || ''))
        if (!agentMsg.text) {
          agentMsg.text = '抱歉，我遇到了一些问题，请稍后重试。'
          acc.onTextDelta(agentMsg.text)
        }
        agentMsg.canResume = true
        agentMsg.status = 'failed'
      } else {
        agentMsg.status = 'interrupted'
        agentMsg.canResume = true
      }
      cur.status = agentMsg.status || 'failed'
      throw e
    } finally {
      agentMsg.streaming = false
      isProcessing.value = false
      abortCtrl = null
      stopTimer()
    }
  }

  const continueRun = async () => {
    const cur = currentTask.value
    if (!cur || isProcessing.value) return
    const lastUser = [...cur.messages].reverse().find((m) => m.role === 'user')
    const prompt =
      '请从中断处继续执行未完成的待办，不要重复已完成步骤。若需入队/发布请先等待审批。'
    await sendMessage(prompt, {
      resume: true,
      initialTodos: currentTodos.value,
      runId: currentRunId.value,
    })
    // keep lastUser context by mentioning — history already has prior messages
    void lastUser
  }

  const deleteTask = async (id) => {
    try {
      await agentApi.deleteSession(id)
    } catch (e) {
      ElMessage.error('删除失败: ' + (e.message || ''))
      return
    }
    tasks.value = tasks.value.filter((t) => t.id !== id)
    if (currentTaskId.value === id) {
      currentTaskId.value = tasks.value.length > 0 ? tasks.value[0].id : null
      if (currentTaskId.value) await selectTask(currentTaskId.value)
    }
  }

  const clearTasks = async () => {
    for (const t of [...tasks.value]) {
      try {
        await agentApi.deleteSession(t.id)
      } catch (e) {
        /* ignore */
      }
    }
    tasks.value = []
    currentTaskId.value = null
  }

  return {
    panelOpen,
    openPanel,
    ensureSessions,
    askInNewConversation,
    tasks,
    currentTaskId,
    currentTask,
    isProcessing,
    autoMode,
    autoPublish,
    sendPageContext,
    activeTab,
    workingLabel,
    elapsedSec,
    currentTodos,
    currentLogs,
    currentRunId,
    currentTurn,
    stepsCount,
    loadSessions,
    createTask,
    selectTask,
    sendMessage,
    continueRun,
    stopStream,
    deleteTask,
    clearTasks,
  }
}
